import React, { useMemo } from 'react';
import { INRWorkoutSessionHit } from 'interfaces/workout';
import { GroupWorkoutHit, GroupBag } from 'interfaces/group-workout';
import { getEvaluatedIntensity } from 'lib/intensity';
import GroupFireIcon from './group-fire-icon'
import './group-group-challenge.scss'

// Extend the INRWorkoutSessionHit interface to include the timestamp
interface ExtendedINRWorkoutSessionHit extends INRWorkoutSessionHit {
  punchedAt: string; // punchedAt remains a string
  punchedAtTimestamp: number; // Add a number version of the timestamp
}

// Props for GroupGroupChallenge component
interface GroupGroupChallengeProps {
  groupBags: GroupBag[];
  groupWorkoutHits: GroupWorkoutHit[];
  weight?: number;
}

// GroupGroupChallenge component
const GroupGroupChallenge: React.FC<GroupGroupChallengeProps> = ({ groupBags, groupWorkoutHits, weight = 75 }) => {
  const cumulativeTotalScore = useMemo(() => {
    if (groupWorkoutHits.length === 0 || groupBags.length === 0) {
      return 0
    }

    // Group hits by bag
    const hitsByBag = groupWorkoutHits.reduce((acc, hit) => {
      const bagId = hit.bag.id;
      if (!acc[bagId]) {
        acc[bagId] = [];
      }
      const sessionHit: ExtendedINRWorkoutSessionHit = {
        angle: 0, // Default value
        force: hit.force,
        height: 0, // Default value
        punchedAt: hit.punchedAt, // Keep the original string
        punchedAtTimestamp: Math.floor(Number(hit.punchedAt) / 1000), // Convert to whole seconds
      };

      acc[bagId].push(sessionHit);
      return acc;
    }, {} as Record<string, ExtendedINRWorkoutSessionHit[]>);

    // Find the minimum and maximum seconds for calculations
    const allTimestamps = groupWorkoutHits.map((hit) =>
      Math.floor(Number(hit.punchedAt) / 1000) // Convert to whole seconds
    );
    const minSecond = Math.min(...allTimestamps);
    const maxSecond = Math.max(...allTimestamps);

    if (!Number.isFinite(minSecond) || !Number.isFinite(maxSecond)) {
      return 0
    }

    const bagWindows = groupBags.map((groupBag) => {
      const bagId = groupBag.bag.id;
      return {
        bagId,
        hits: [...(hitsByBag[bagId] || [])].sort(
          (a, b) => a.punchedAtTimestamp - b.punchedAtTimestamp
        ),
        startIndex: 0,
        endIndex: 0,
      };
    });

    let totalScore = 0

    for (let currentSecond = minSecond; currentSecond <= maxSecond; currentSecond++) {
      for (const bagWindow of bagWindows) {
        const { hits } = bagWindow

        while (
          bagWindow.startIndex < hits.length &&
          hits[bagWindow.startIndex].punchedAtTimestamp < currentSecond - 10
        ) {
          bagWindow.startIndex += 1
        }

        while (
          bagWindow.endIndex < hits.length &&
          hits[bagWindow.endIndex].punchedAtTimestamp <= currentSecond
        ) {
          bagWindow.endIndex += 1
        }

        const relevantHits = hits.slice(bagWindow.startIndex, bagWindow.endIndex)
        const excerciseSecond = Math.min(currentSecond - minSecond, 10)
        let intensity = getEvaluatedIntensity(relevantHits, weight, excerciseSecond)

        // Adjust the intensity by subtracting 1
        intensity = Math.max(0, intensity - 1)
        totalScore += intensity
      }
    }

    return totalScore
  }, [groupBags, groupWorkoutHits, weight]);
  // const cumulativeTotalScore = 99999
  return (
    <div data-testid="group-group-challenge" data-score={cumulativeTotalScore}>
      <h2 className="group-group-title">INTENSITY POINTS GROUP</h2>
      <h3 className="group-group-score">
        <span className="score-number">{cumulativeTotalScore}</span>
        <span className="score-icon"><GroupFireIcon /></span>
      </h3>
      {/* <table border={1} cellPadding={5}>
        <thead>
          <tr>
            <th>Second</th>
            {groupBags.map((groupBag) => (
              <th key={groupBag.bag.id}>Bag {groupBag.bag.id}</th>
            ))}
            <th>Total Score</th>
          </tr>
        </thead>
        <tbody>
          {sortedScores.map(({ second, total, ...bagScores }) => (
            <tr key={second}>
              <td>{second}</td>
              {groupBags.map((groupBag) => (
                <td key={groupBag.bag.id}>{bagScores[groupBag.bag.id] || 0}</td>
              ))}
              <td>{total}</td>
            </tr>
          ))}
        </tbody>
      </table> */}
    </div>
  );
};

export default React.memo(GroupGroupChallenge);
